import React, { Component } from 'react';
import PropTypes from 'prop-types';
import AlertMessage from './AlertMessage';
import { AlertMessageTypes } from '../constants/index';
import PageBanner from './PageFooter';
import PageFooter from './PageFooter';
import PageHeader from './PageHeader';
import ExplainerText from './ExplainerText';
import { PageHeaderButtonPropType } from './PageHeaderButton';
import { formatClassName } from '../Util';
import PageWrapper from '../components/Layout/PageWrapper';
import Header from '../components/Layout/Header';
import Section from '../components/Layout/Section';
import Wrapper from './Layout/Wrapper';
import Footer from './Layout/Footer';

/** renders the standard page layout that is used in Foundry */
export default class Page extends Component {
  static propTypes = {
    /** configures the action buttons rendered in the PageHeader
     *  (passed to PageHeader -> PageHeaderButton component) */
    actionButton: PageHeaderButtonPropType,
    /** renders a back button in the PageHeader - true for default behavior
     *  or a function to be called instead
     *  (passed to PageHeader component) */
    backButton: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.func
    ]),
    /** renders a PageBanner inside of the PageHeader
     *  (passed to PageHeader component) */
    banner: PropTypes.instanceOf(PageBanner),
    /** children will be rendered inside the page body */
    children: PropTypes.any,
    /** any additional classNames to add to the page container */
    className: PropTypes.string,
    /** action that clears all stored messages
     *  (passed to AlertMessage component) */
    clearMessages: PropTypes.func,
    /** adds class styles based on the type of page content */
    contentType: PropTypes.oneOf(['cards', 'fullwidth', 'complex', 'list']),
    /** error message to display in an alert banner */
    errorMessage: PropTypes.string,
    /** adds a class to the PageFooter button container
     *  (passed to PageFooter component) */
    footerButtonBarClass: PropTypes.string,
    /** configures the buttons rendered in the PageFooter
     *  (passed to PageFooter component) */
    footerButtons: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.func
      ])
    ),
    /** adds a class to the PageFooter wrapper
     *  (passed to PageFooter component) */
    footerClass: PropTypes.string,
    /** adds a class to the PageHeader wrapper
     *  (passed through to PageHeader component) */
    headingClass: PropTypes.string,
    /** used to render tabs inside the PageHeader
     *  (passed to PageHeader component) */
    headerTabs: PropTypes.element,
    /** adds a zmdi icon in front of the page title
     *  (passed to PageHeader component) */
    icon: PropTypes.string,
    /** text that appears just above the page content */
    instructionalText: PropTypes.string,
    /** adds class styles depending whether this is a parent or child page */
    pageType: PropTypes.oneOf(['parent', 'child']),
    /** add classes passed through props to `div.page-body.row` */
    pageBodyClassName: PropTypes.string,
    /** displays success and error messages if true */
    showAlertMessages: PropTypes.bool,
    /** title displayed at the top of the page and sets the
     *  document window title
     *  (passed through to PageHeader component) */
    title: PropTypes.string,
    /** renders a Toolbar inside of the PageHeader
     *  (passed to PageHeader component) */
    toolbar: PropTypes.element,
    /** success message to display in an alert banner */
    successMessage: PropTypes.string,
    /** set to false to hide the header */
    showHeader: PropTypes.bool,
    /** overrides the value set for the document window title
     *  (passed to PageHeader component) */
    overrideTitle: PropTypes.string,
    usePageWrapper: PropTypes.bool,
    nestedPage: PropTypes.bool
  }

  static defaultProps = {
    contentType: "fullwidth",
    nestedPage: false,
    pageType: "parent",
    showAlertMessages: true,
    showHeader: true,
    usePageWrapper: false
  };

  get pageClassName() {
    const { pageType, contentType, className } = this.props;
    return formatClassName([
      'page-template',
      pageType,
      contentType,
      className
    ]);
  }

  get pageBodyClassName() {
    const { banner, footerButtons, headerTabs, pageBodyClassName, showHeader, toolbar, usePageWrapper } = this.props;

    return formatClassName([
      `page-body ${ !usePageWrapper ?  'row' : '' }`,
      pageBodyClassName,
      {
        banner,
        'has-footer': footerButtons,
        'header-tabs': headerTabs,
        'no-header': !showHeader,
        toolbar
      }
    ]);
  }

  renderAlertMessage = (message, messageType) => {
    const { clearMessages, showAlertMessages } = this.props;
    if (!message || !showAlertMessages) {
      return null;
    }
    return (
      <AlertMessage message={message} messageType={messageType}
        visible={message} clearMessages={clearMessages} />
    );
  }

  renderPageHeader = () => {
    const { actionButton, backButton, banner, headerTabs, headingClass, icon,
      overrideTitle, showHeader, title, toolbar, usePageWrapper } = this.props;
    if (!showHeader) {
      return null;
    }

    return (usePageWrapper ?
      <Header
        back={backButton}
        button={actionButton}
        title={title}
        icon={icon}
        //headingClass={headingClass}
        //overrideTitle={overrideTitle}
      >
        {headerTabs}
        {toolbar}
        {banner}
      </Header> :
      <PageHeader
        actionButton={actionButton}
        backButton={backButton}
        title={title}
        icon={icon}
        banner={banner}
        headerTabs={headerTabs}
        headingClass={headingClass}
        overrideTitle={overrideTitle}
        toolbar={toolbar}
      />);
  }

  renderPageFooter = () => {
    const { footerButtonBarClass, footerButtons, footerClass, usePageWrapper } = this.props;
    if (!footerButtons) {
      return null;
    }
    return (
      usePageWrapper ?
        <Footer
          button={footerButtons}
          buttonClass={footerClass}
        /> :
        <PageFooter
          footerButtonBarClass={footerButtonBarClass}
          footerButtons={footerButtons}
          footerClass={footerClass}
        />
    );
  }

  renderPageBody = () => {
    const { children, instructionalText, usePageWrapper, contentType } = this.props;
    return (
      usePageWrapper ?
        <Section className={this.pageBodyClassName} type={contentType}>
          {instructionalText &&
            <ExplainerText>
              {instructionalText}
            </ExplainerText>}
          {children}
        </Section>  :
        <div className={this.pageBodyClassName}>
          {instructionalText &&
            <div className="instructional-text">
              {instructionalText}
            </div>}
          {children}
        </div>
    );
  }

  renderPageContent = () => {
    const { errorMessage, successMessage } = this.props;
    return (<>
      {this.renderAlertMessage(errorMessage, AlertMessageTypes.danger)}
      {this.renderAlertMessage(successMessage, AlertMessageTypes.success)}
      {this.renderPageHeader()}
      {this.renderPageBody()}
      {this.renderPageFooter()}
    </>);
  }

  render() {
    const { usePageWrapper, nestedPage } = this.props;
    return (
      usePageWrapper ?
        nestedPage ?
          <Wrapper>
            {this.renderPageContent()}
          </Wrapper>
          :
          <PageWrapper>
            {this.renderPageContent()}
          </PageWrapper>
        :
        <div className={this.pageClassName}>
          {this.renderPageContent()}
        </div>
    );
  }
}
